// frontend/admin-portal/src/services/purchaseOrderPdfService.js
import { renderToStaticMarkup } from "react-dom/server";
import React from "react";
import { PurchaseOrderTemplate } from "@fiberhome/shared-ui";

/**
 * Render the PO template to a standalone HTML document and trigger
 * a print dialog. The user's browser "Save as PDF" produces the actual
 * PDF. Also returns a Blob URL the caller can offer as a download.
 */
export function renderPOtoHtml({ po, supplier, store }) {
    const markup = renderToStaticMarkup(
        React.createElement(PurchaseOrderTemplate, { po, supplier, store })
    );

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Purchase Order ${po?.code || ""}</title>
<style>
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; }
  @media print {
    @page { margin: 1cm; }
  }
  table { page-break-inside: auto; }
  tr { page-break-inside: avoid; }
</style>
</head>
<body>
${markup}
<script>
  window.addEventListener('load', function() {
    // Give the browser a tick to render before firing print
    setTimeout(function() { window.print(); }, 200);
  });
</script>
</body>
</html>`;
}

/**
 * Opens the PO in a new tab where the user can print or save as PDF.
 * Also returns the Blob URL so the caller can offer "Download HTML" as
 * an alternative for email clients that don't attach PDFs well.
 */
export function openPurchaseOrderPdf({ po, supplier, store }) {
    const html = renderPOtoHtml({ po, supplier, store });
    const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    return url;
}

/**
 * Construct a mailto: link pre-filled with the PO summary and supplier
 * email. The user's mail client opens with the subject/body filled in;
 * they can attach the PDF they just downloaded.
 */
export function buildSupplierMailto({ po, supplier, store }) {
    const to = supplier?.email || "";
    const subject = `Purchase Order ${po?.code || ""} — ${store?.name || "FiberHome"}`;

    const lines = [
        `Dear ${supplier?.contactName || supplier?.name || "Supplier"},`,
        ``,
        `Please find our purchase order ${po?.code || ""} below.`,
        ``,
        `Expected delivery: ${po?.expectedDelivery
            ? new Date(po.expectedDelivery).toLocaleDateString()
            : "to be confirmed"
        }`,
        ``,
        `Items:`,
    ];

    for (const it of po?.items || []) {
        lines.push(
            `  • ${it.productName || it.productSku || "item"} — ${it.quantity} ${it.unit || ""} @ ${it.unitPrice} ${po?.currency || "USD"}`
        );
    }

    lines.push(``, `Total: ${po?.totalAmount || po?.total || 0} ${po?.currency || "USD"}`);
    if (po?.notes) {
        lines.push(``, `Notes:`, po.notes);
    }
    lines.push(``, `Please reply to confirm.`);
    lines.push(``, `Best regards,`, store?.name || "FiberHome");

    const body = lines.join("\n");
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}